import { deepFreeze } from '../deepFreeze'
import { type Subscribable, subscribableCreate } from '../subscribable'

/** Store with subscription, read and write */
export type Store<T> = Subscribable & {
	/**
	 * Get the current state of the store
	 */
	getState: () => Readonly<T>
	/**
	 * Reset the store to its initial state, optionally overriding some values.
	 */
	reset: (stateOverride?: Partial<T>) => void
	/**
	 * Set values in the store.
	 */
	set: (data: Partial<T>) => void
}

/** Store with subscription and read */
export type StoreReadOnly<T> = Pick<Store<T>, 'subscribe' | 'getState'>
/** Infer the underlying state-type of a store-type */
export type StoreState<T extends StoreReadOnly<any>> = T extends StoreReadOnly<infer State> ? State : never

/**
 * Create a simple store, with an interface somewhat resembling redux
 */
export const storeCreate = <T extends {}>(initialState: T): Store<T> => {
	let state = deepFreeze(initialState)

	// Create comparison function to see if state changes during an update
	const sameStateFilter = (comparison: T) => () => comparison === state

	const { update, subscribe } = subscribableCreate()

	return {
		reset: (stateOverride = {}) => {
			state = deepFreeze({
				...initialState,
				...stateOverride,
			})

			update(undefined, { continueUpdate: sameStateFilter(state) })
		},
		set: (data) => {
			state = deepFreeze({
				...state,
				...data,
			})

			update(undefined, { continueUpdate: sameStateFilter(state) })
		},
		getState: () => state,
		subscribe,
	}
}
